import {HStack, memoWithType, VStack} from "@hosttools/frontend";
import {useCallback, useMemo} from "react";

import LockListingItem from "./LockListingItem";
import type {LockListing} from "./type";

import type {OptionType} from "@/client/components/Select";
import Select from "@/client/components/Select";

type Props<T extends LockListing, O extends OptionType> = {
    options: O[];
    selectedItems?: T[];
    disabled?: boolean;
    onSelectOption: (option: O) => void;
    onRemoveItem: (item: T) => void;
};

function LockListingSelect<T extends LockListing, O extends OptionType>({
    options,
    selectedItems,
    disabled,
    onSelectOption,
    onRemoveItem
}: Props<T, O>) {
    const handleSelectOption = useCallback(
        (option: O | null) => {
            if (option) {
                onSelectOption(option);
            }
        },
        [onSelectOption]
    );

    const sortedOptions = useMemo(() => {
        return options.sort((a, b) => {
            let labelA = a.label;
            let labelB = b.label;
            // Make sure the label is defined
            if (!labelA || !labelB) {
                return 0;
            }
            labelA = labelA.toLowerCase();
            labelB = labelB.toLowerCase();
            if (labelA < labelB) {
                return -1;
            }
            if (labelA > labelB) {
                return 1;
            }
            return 0;
        });
    }, [options]);

    return (
        <VStack space="sm">
            <Select
                // this tweak makes the selected values on different places
                // I think it's best to set `isMulti: true` to leverage the multiple selection of select component
                isDisabled={disabled}
                maxMenuHeight={190}
                options={sortedOptions}
                value={null}
                onChange={handleSelectOption}
            />
            {selectedItems && selectedItems.length !== 0 && (
                <HStack className="flex flex-wrap" space="sm">
                    {selectedItems.map(item => (
                        <LockListingItem
                            key={item._id}
                            item={item}
                            onRemove={disabled ? undefined : onRemoveItem}
                        />
                    ))}
                </HStack>
            )}
        </VStack>
    );
}

export default memoWithType(LockListingSelect);
